import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Code2,
  Smartphone,
  BrainCircuit,
  Cloud,
  ShieldCheck,
  Rocket,
  ArrowRight,
  Sparkles,
  ChevronRight,
  Briefcase,
} from "lucide-react";

import { ourWorkProjects } from "@/data/ourWork";
import PageHero from "@/components/shared/PageHero";
import Section from "@/components/shared/Section";
import SectionHeader from "@/components/shared/SectionHeader";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import Eyebrow from "@/components/shared/Eyebrow";

const PILLARS = [
  { icon: Smartphone,   title: "Mobile Experiences",       desc: "iOS and Android apps built for performance, security, and scale." },
  { icon: BrainCircuit, title: "AI-Integrated Products",   desc: "Production-grade AI features embedded into real workflows." },
  { icon: Cloud,        title: "Cloud & Data Platforms",   desc: "Reliable data infrastructure and analytics that power smarter decisions." },
  { icon: ShieldCheck,  title: "Risk & Compliance",        desc: "Governed AI with auditability, explainability, and controls." },
  { icon: Code2,        title: "Custom Software",          desc: "Tailored systems built around your operating model." },
  { icon: Rocket,       title: "Automation at Scale",      desc: "Workflows that reduce cycle times and boost team output." },
];

const TEAM = [
  { title: "iOS Developers",            desc: "Swift and SwiftUI specialists focused on secure, polished mobile experiences." },
  { title: "Android Developers",        desc: "Kotlin-first engineering for scalable Android apps with fast iteration cycles." },
  { title: "AI-Integrated Developers",  desc: "AI features embedded into products with reliable model integration and monitoring." },
];

const OurWork = () => {
  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      <PageHero
        eyebrow="Our Work"
        eyebrowIcon={Briefcase}
        title={
          <>
            Showcase &{" "}
            <span className="gradient-text">portfolio of impact</span>
          </>
        }
        description="A high-level look at the products, platforms, and experiences we've built — focused on outcomes, delivery, and credibility."
        actions={
          <>
            <Button asChild className="btn-primary rounded-full px-6 h-11">
              <Link to="/case-studies" className="inline-flex items-center gap-2">
                View case studies <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-outline rounded-full px-6 h-11">
              <Link to="/contact">Start a project</Link>
            </Button>
          </>
        }
      />

      {/* Portfolio */}
      <Section>
        <SectionHeader
          eyebrow="Portfolio"
          eyebrowIcon={Sparkles}
          title={<>Selected <span className="gradient-text">work</span></>}
          description="Quick snapshots of applications, websites, and AI platforms we've delivered."
          align="left"
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {ourWorkProjects.map((project, i) => (
            <Reveal key={project.slug} variant="up" delay={(i % 3) * 0.06}>
              <GlowCard className="p-0 overflow-hidden h-full flex flex-col">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute inset-0 p-5 flex flex-col justify-end">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-white/80">{project.client}</p>
                    <h3 className="font-display font-bold text-lg text-white leading-tight mt-1">{project.title}</h3>
                    <p className="text-xs font-medium text-cyan-300 mt-0.5">{project.type}</p>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="text-[var(--text-secondary)] text-sm mb-4 leading-relaxed line-clamp-3">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {project.services.map((service) => (
                      <span
                        key={service}
                        className="text-[11px] font-medium text-[var(--text-secondary)] border border-[color:var(--border-default)] bg-[color:var(--surface-1)] rounded-full px-2.5 py-1"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto">
                    <Button asChild className="btn-primary rounded-full px-5 h-9 w-full">
                      <Link to={`/our-work/${project.slug}`} className="inline-flex items-center gap-2 text-sm">
                        View project <ChevronRight className="w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Pillars */}
      <Section>
        <SectionHeader
          eyebrow="What we deliver"
          eyebrowIcon={Code2}
          title={<>End-to-end <span className="gradient-text">capabilities</span></>}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} variant="up" delay={(i % 3) * 0.06}>
              <GlowCard className="p-7 h-full group">
                <div className="feature-icon feature-icon-lg mb-5 group-hover:scale-110 transition-transform">
                  <p.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{p.title}</h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{p.desc}</p>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Team */}
      <Section>
        <Reveal variant="up">
          <GlowCard variant="liquid" className="aurora p-8 sm:p-12 overflow-hidden">
            <div className="relative z-10">
              <Eyebrow icon={Sparkles} className="mb-4">Expert team</Eyebrow>
              <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3">
                A 50+ member <span className="gradient-text">delivery team</span>
              </h2>
              <p className="text-[var(--text-secondary)] mb-8 max-w-2xl">
                Expert iOS developers, Android developers, and AI-integrated engineers who build production-ready
                mobile apps and intelligent systems.
              </p>
              <div className="grid sm:grid-cols-3 gap-5">
                {TEAM.map((t, i) => (
                  <Reveal key={t.title} variant="up" delay={i * 0.08}>
                    <GlowCard className="p-6 h-full">
                      <h3 className="font-semibold mb-2">{t.title}</h3>
                      <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{t.desc}</p>
                    </GlowCard>
                  </Reveal>
                ))}
              </div>
            </div>
          </GlowCard>
        </Reveal>
      </Section>

      {/* CTA */}
      <Section>
        <Reveal variant="up">
          <GlowCard variant="liquid" className="aurora p-10 text-center max-w-4xl mx-auto">
            <Eyebrow icon={Sparkles} className="mb-4">Build with us</Eyebrow>
            <h3 className="font-display font-bold text-3xl sm:text-4xl mb-3 leading-tight">
              Ready to build something <span className="gradient-text">impactful?</span>
            </h3>
            <p className="text-[var(--text-secondary)] mb-7 max-w-xl mx-auto">
              Tell us about your goals and we'll align the right team for your product roadmap.
            </p>
            <Button asChild className="btn-primary rounded-full px-8 h-12">
              <Link to="/contact" className="inline-flex items-center gap-2">
                Start a project <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </GlowCard>
        </Reveal>
      </Section>
    </div>
  );
};

export default OurWork;
